import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { Toast } from "primereact/toast";
import React, { createContext, Ref, useContext, useReducer, useRef } from "react";

interface IForgotPasswordState {
    isOpen: boolean;
    email: string;
    errorMessage: string;
    showChangePassword: boolean;
    password: string;
    code: string;
}

interface IForgotPasswordContext extends IForgotPasswordState {
    open: (email: string) => void;
    close: () => void;
    changeEmail: (email: string) => void;
    changeCode: (code: string) => void;
    changePassword: (password: string) => void;
    requestForgot: () => void;
    requestChangePassword: () => void;
    isLoading: boolean;
    toast: Ref<Toast>;
}

const initialState: IForgotPasswordState = {
    isOpen: false,
    email: "",
    errorMessage: "",
    showChangePassword: false,
    password: "",
    code: ""
}

const ForgotPasswordContext = createContext<IForgotPasswordContext|null>(null);

const reducer = (state: IForgotPasswordState, action: { type: string; payload: string; }) => {
    switch (action.type) {
        case "Open":
            return {
                ...state,
                errorMessage: "",
                isOpen: true,
                email: action.payload
            };
        case "Close":
            return {
                ...initialState
            }
        case "ChangeEmail":
            return {
                ...state,
                errorMessage: "",
                email: action.payload
            }
        case "ChoosePassword":
            return {
                ...state,
                errorMessage: "",
                showChangePassword: true
            }
        case "ChangePassword":
            return {
                ...state,
                password: action.payload
            }
        case "ChangeCode":
            return {
                ...state,
                code: action.payload
            }
        case "Error":
            return {
                ...state,
                errorMessage: action.payload
            }
        default:
            throw new Error(`Not implemented type ${action.type}`);
    }
}

interface IForgotPasswordProviderProps {
    children: React.ReactNode;
}

const ForgotPasswordProvider = ({ children }: IForgotPasswordProviderProps) => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const toast = useRef<Toast|null>(null);

    const requestForgotMutation = useMutation({
        mutationFn: async (email: string) => {
            return axios.post(`${import.meta.env.VITE_API_URL}/user/forgot_password`, 
                { 
                    username: email 
                },
                {
                    headers: {
                        "Content-Type": "text/plain"
                    }
                });
        }
    });

    const requestChangePasswordMutation = useMutation({
        mutationFn: async ({email, password, code} :{ email: string, password: string, code: string }) => {
            return axios.post(`${import.meta.env.VITE_API_URL}/user/reset_password`, 
                { 
                    username: email,
                    password: password,
                    code: code
                },
                {
                    headers: {
                        "Content-Type": "text/plain"
                    }
                });
        }
    });

    const open = (email: string) => {
        dispatch({ type: "Open", payload: email });
    }

    const close = () => {
        dispatch({ type: "Close", payload: "" });
    }

    const changeEmail = (email: string) => {
        dispatch({ type: "ChangeEmail", payload: email });
    }

    const changeCode = (code: string) => {
        dispatch({ type: "ChangeCode", payload: code });
    }

    const changePassword = (password: string) => {
        dispatch({ type: "ChangePassword", payload: password });
    }

    const requestForgot = async () => {
        try {
            await requestForgotMutation.mutateAsync(state.email);
            dispatch({ type: "ChoosePassword", payload: "" });
        } catch (err) {
            console.error(err);
            dispatch({ type: "Error", payload: (err as Error).message });
        }
    }

    const requestChangePassword = async () => {
        try {
            await requestChangePasswordMutation.mutateAsync({ ...state });
            dispatch({ type: "Close", payload: "" });

            toast.current?.show(
                { 
                    severity: "success", 
                    summary: "Senha alterada com sucesso.", 
                    detail: "Faça login com a nova senha", 
                    life: 4000 
                });
        } catch (err) {
            console.error(err);
            dispatch({ type: "Error", payload: (err as Error).message });
        }
    }
    
    return (
        <ForgotPasswordContext.Provider 
            value={{ 
                ...state, 
                open, 
                close, 
                changeEmail, 
                changeCode,
                changePassword,
                requestForgot,
                requestChangePassword,
                isLoading: requestForgotMutation.isPending || requestChangePasswordMutation.isPending,
                toast
            }}
        >
            {children}
        </ForgotPasswordContext.Provider>
    );
}

const useForgotPassword = () => {
    const context = useContext(ForgotPasswordContext);
    if (context === undefined)
      throw new Error("ForgotPasswordContext was used outside the ForgotPasswordProvider");
    return context;
}

export { ForgotPasswordProvider, useForgotPassword };
